import { apiClient } from '@shared/utils/api-client';
import type { Category, CreateCategoryDto, UpdateCategoryDto } from '../types';

export const categoriesApi = {
  create: async (data: CreateCategoryDto, file?: File): Promise<Category> => {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('isNotFlower', String(data.isNotFlower === true));
    if (data.sectionId) {
      formData.append('sectionId', data.sectionId.toString());
    }
    if (file) {
      formData.append('image', file);
    }
    return apiClient.post<Category>('/categories', formData);
  },

  getAll: async (sectionId?: number): Promise<Category[]> => {
    const url = sectionId ? `/categories?sectionId=${sectionId}` : '/categories';
    return apiClient.get<Category[]>(url);
  },

  getAllGroupedBySection: async (): Promise<Record<number, Category[]>> => {
    return apiClient.get<Record<number, Category[]>>('/categories/grouped-by-section');
  },

  getPopular: async (): Promise<Category[]> => {
    return apiClient.get<Category[]>('/categories/collections/popular');
  },

  runSyncPopularCron: async (): Promise<{
    marked: number;
    ids: number[];
    ranks: Array<{ id: number; popularRank: number }>;
  }> => {
    return apiClient.post('/categories/collections/popular/sync-flags');
  },

  getWithoutSections: async (): Promise<Category[]> => {
    return apiClient.get<Category[]>('/categories/without-sections');
  },

  getById: async (id: number): Promise<Category> => {
    return apiClient.get<Category>(`/categories/${id}`);
  },

  update: async (id: number, data: UpdateCategoryDto, file?: File): Promise<Category> => {
    const formData = new FormData();
    if (data.name !== undefined) {
      formData.append('name', data.name);
    }
    if (data.isNotFlower !== undefined) {
      formData.append('isNotFlower', String(data.isNotFlower));
    }
    if (data.sectionId !== undefined) {
      formData.append('sectionId', data.sectionId.toString());
    }
    if (data.shouldDeleteImage !== undefined) {
      formData.append('shouldDeleteImage', String(data.shouldDeleteImage));
    }
    if (file) {
      formData.append('image', file);
    }
    return apiClient.put<Category>(`/categories/${id}`, formData);
  },

  setHidden: async (id: number, isHidden: boolean): Promise<Category> => {
    return apiClient.patch<Category>(`/categories/${id}/hidden`, { isHidden });
  },

  setPopular: async (id: number, isPopular: boolean): Promise<Category> => {
    return apiClient.patch<Category>(`/categories/${id}/popular`, { isPopular });
  },

  delete: async (id: number): Promise<void> => {
    return apiClient.delete(`/categories/${id}`);
  },

  addToSection: async (id: number, sectionId: number): Promise<Category> => {
    return apiClient.post<Category>(`/categories/${id}/sections`, { sectionId });
  },

  removeFromSection: async (id: number, sectionId: number): Promise<Category> => {
    return apiClient.delete<Category>(`/categories/${id}/sections`, { sectionId });
  },
};

